import * as React from 'react';
import propz from 'propz';
import { Loader } from 'Src/components/Loader/Loader';
import { Button } from 'Src/components/Button/Button';
import { QuestionsTemplateField } from 'Src/components/QuestionsTemplateField/QuestionsTemplateField';
import { ConfirmationRequestTemplate, ConfirmationRequestTemplateField } from 'Src/models/confirmationRequestTemplate';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { superAdminGet, superAdminPut } from 'Src/helpers/service/superadmin/api';
import { ReactSortable } from 'react-sortablejs';
import { FormBuilderV2 } from 'Src/components/FormBuilderV2/FormBuilderV2';
import { formDefinitionToLegacyFields } from 'Src/helpers/form/legacyFormAdapter';
import { uploadFormImageAsSuperAdmin } from 'Src/helpers/form/formMedia';

function getTemplate(schoolId: string, endpoint: string): Promise<ConfirmationRequestTemplate> {
  return superAdminGet<ConfirmationRequestTemplate>(`/schools/${schoolId}/${endpoint}`);
}

function saveTemplate(
  schoolId: string,
  endpoint: string,
  fields: ConfirmationRequestTemplateField[],
  template?: ConfirmationRequestTemplate
): Promise<ConfirmationRequestTemplate> {
  const data = fields.map(field => {
    const isEnumType = field.type === QUESTIONS_TEMPLATE_TYPE.ENUM;
    const result = { ...field } as any;
    delete result._id;

    if (!result.heading) delete result.heading;

    if (isEnumType) {
      result.enumOptions = field.enumOptions
        .map(o => o.trim())
        .join(',')
        .split(',')
        .filter(o => o !== '');
    } else {
      delete result.enumOptions;
    }

    return result;
  });

  return superAdminPut<ConfirmationRequestTemplate>(`/schools/${schoolId}/${endpoint}`, {
    fields: data,
    ...(template?.schemaVersion === 2 && template.formDefinition
      ? {
          schemaVersion: 2,
          formDefinition: template.formDefinition
        }
      : {})
  });
}

interface EditorProps {
  schoolId: string;
  title: string;
  description: string;
  endpoint: string;
}

interface EditorState {
  isLoading: boolean;
  isSaving: boolean;
  template: ConfirmationRequestTemplate | undefined;
  errorMessage: string;
  successMessage: string;
}

class TemplateEditor extends React.Component<EditorProps, EditorState> {
  constructor(props: EditorProps) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      template: undefined,
      errorMessage: '',
      successMessage: ''
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
    getTemplate(this.props.schoolId, this.props.endpoint)
      .then(template => {
        this.setState({ isLoading: false, template });
      })
      .catch(err => {
        console.error('Cannot load template', err);
        this.setState({ isLoading: false, errorMessage: 'Could not load template.' });
      });
  };

  onFieldsChange = (fieldId: string, objectKey: string, value: any) => {
    const { template } = this.state;
    const fields = propz.get(template, ['fields'], []);
    const fieldsUpdated = fields.map(field => (field._id === fieldId ? { ...field, [objectKey]: value } : field));
    this.setState({ template: { ...template, fields: fieldsUpdated } });
  };

  onRemoveFieldClick = (fieldId: string) => {
    const { template } = this.state;
    const fields = propz.get(template, ['fields'], []);
    this.setState({ template: { ...template, fields: fields.filter(f => f._id !== fieldId) } });
  };

  onAddFieldClick = () => {
    const { template } = this.state;
    const fields = propz.get(template, ['fields'], []);
    const newField: ConfirmationRequestTemplateField = {
      _id: String(new Date().getTime()),
      heading: '',
      isDefault: false,
      isRequired: false,
      type: QUESTIONS_TEMPLATE_TYPE.STRING as any,
      enumOptions: []
    };
    this.setState({ template: { ...template, fields: [...fields, newField] } });
  };

  onSubmit = () => {
    const { template } = this.state;
    const fields = propz.get(template, ['fields'], []);
    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    saveTemplate(this.props.schoolId, this.props.endpoint, fields, template)
      .then(saved => {
        this.setState({ isSaving: false, template: saved, successMessage: 'Template saved successfully.' });
      })
      .catch(err => {
        console.error('Cannot save template', err);
        this.setState({ isSaving: false, errorMessage: 'Could not save template. Please try again.' });
      });
  };

  render() {
    const { title, description } = this.props;
    const { isLoading, isSaving, template, errorMessage, successMessage } = this.state;
    const fields = propz.get(template, ['fields'], []);

    return (
      <div className="card mb-4">
        <div className="card-header">
          <strong>{title}</strong>
          <div className="text-muted small">{description}</div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
              {successMessage && <div className="alert alert-success">{successMessage}</div>}

              {template?.schemaVersion === 2 && template.formDefinition ? (
                <FormBuilderV2
                  definition={template.formDefinition}
                  uploadImage={uploadFormImageAsSuperAdmin}
                  onChange={formDefinition =>
                    this.setState({
                      template: {
                        ...template,
                        schemaVersion: 2,
                        formDefinition,
                        fields: formDefinitionToLegacyFields(formDefinition) as any
                      }
                    })
                  }
                />
              ) : (
              <ReactSortable
                list={fields.map(f => ({ ...f, id: f._id }))}
                setList={sorted => {
                  this.setState({ template: { ...template, fields: sorted } });
                }}
                handle=".eDragHandle"
                animation={150}
                className="row mt-2"
              >
                {fields.map(field => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3" key={field._id}>
                    <div
                      className="eDragHandle"
                      style={{
                        cursor: 'grab',
                        textAlign: 'center',
                        color: '#aaa',
                        marginBottom: 4,
                        userSelect: 'none'
                      }}
                    >
                      ⋮⋮
                    </div>
                    <QuestionsTemplateField
                      field={field}
                      onChange={this.onFieldsChange}
                      onRemove={this.onRemoveFieldClick}
                    />
                  </div>
                ))}
              </ReactSortable>
              )}

              <div className="d-flex justify-content-between mt-3">
                {template?.schemaVersion !== 2 ? <Button text="Add field" onClick={this.onAddFieldClick} /> : <span />}
                <div>
                  <Button text="Cancel" onClick={this.load} customClass="btn-secondary mr-2" />
                  <Button text="Save" onClick={this.onSubmit} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

interface Props {
  schoolId: string;
}

export class SuperAdminSchoolTemplates extends React.Component<Props> {
  render() {
    const { schoolId } = this.props;
    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Templates</h4>
          <div className="text-muted small">
            Configure consent request and club booking form templates for this school.
          </div>
        </div>

        <TemplateEditor
          schoolId={schoolId}
          title="Consent request template"
          description="Fields shown in consent/permission request forms sent to parents."
          endpoint="template"
        />

        <TemplateEditor
          schoolId={schoolId}
          title="Confirmation request template"
          description="Fields shown in event confirmation request forms."
          endpoint="confirmationRequestTemplate"
        />

        <TemplateEditor
          schoolId={schoolId}
          title="Club booking form template"
          description="Fields shown in club booking / confirmation request forms."
          endpoint="clubConfirmationRequestTemplate"
        />
      </div>
    );
  }
}

